import React, { useId } from 'react'
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar, Rectangle } from 'recharts'

import { getForceGraphData, getXaxisTicks, getAverageForcePercent, getRoundBarsData } from 'lib/graphs'
import { ForceGraphRoundProps } from 'interfaces/graph'
import CustomYAxis from './custom-y-axis'
import CustomXAxis from './custom-x-axis'
import { getCombinationHitColor, getRoundDisplayForceHits, roundUsesCombinationData } from 'lib/hit-combinations'

interface CombinationBarShapeProps {
  fill?: string
  height?: number
  payload?: ReturnType<typeof getForceGraphData>[number]
  width?: number
  x?: number
  y?: number
}

export const CombinationBarShape = ({
  fill = '#00BFB9',
  height = 0,
  payload,
  width = 0,
  x = 0,
  y = 0,
}: CombinationBarShapeProps) => {
  const reactId = useId().replace(/:/g, '')

  if (!payload?.force || !height) return null

  if (!payload.combination) {
    return <Rectangle fill={fill} height={height} radius={[10, 10, 0, 0]} width={width} x={x} y={y} />
  }

  const clipPathId = `combination-bar-${reactId}`
  let renderedForce = 0

  return (
    <g data-segment-count={payload.segments.length}>
      <defs>
        <clipPath id={clipPathId}>
          <rect height={height} rx={Math.min(10, width / 2)} width={width} x={x} y={y} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipPathId})`}>
        {payload.segments.map((hit, index) => {
          const segmentHeight = (hit.force / payload.force!) * height
          renderedForce += hit.force
          const segmentY = y + height - (renderedForce / payload.force!) * height

          return (
            <rect
              data-force={hit.force}
              fill={getCombinationHitColor(index)}
              height={segmentHeight}
              key={`${hit.punchedAt}-${index}`}
              width={width}
              x={x}
              y={segmentY}
            />
          )
        })}
      </g>
      {payload.segments.slice(0, -1).map((_, index) => {
        const forceAtBoundary = payload.segments
          .slice(0, index + 1)
          .reduce((total, segment) => total + segment.force, 0)
        const dividerY = y + height - (forceAtBoundary / payload.force!) * height

        return (
          <line
            data-combination-divider="true"
            key={`divider-${index}`}
            stroke="white"
            strokeWidth={1}
            x1={x}
            x2={x + width}
            y1={dividerY}
            y2={dividerY}
          />
        )
      })}
    </g>
  )
}

const ForceGraphRound = ({
  roundData,
  maxForce,
  roundTime,
  roundComplete = false,
  graphWidth,
}: ForceGraphRoundProps) => {
  const currentRoundHits = getRoundDisplayForceHits(roundData)
  const avg = getAverageForcePercent(currentRoundHits)
  const showCombinations = roundUsesCombinationData(roundData)
  const barsArray = getRoundBarsData(roundData)
  const forceGraphData = getForceGraphData(barsArray)

  const firstValue = 0
  const lastValue = roundTime
  const { secondValue, thirdValue, fourthValue } = getXaxisTicks(firstValue, lastValue)

  const xAxisTicks = [firstValue, secondValue, thirdValue, fourthValue, lastValue]

  return (
    <>
      {roundComplete && (
        <>
          <CustomYAxis
            maxForce={maxForce}
            isForceGraph={true}
            roundComplete={roundComplete}
            avg={avg}
            showReferenceLines={!showCombinations}
          />
          <CustomXAxis roundComplete={roundComplete} displayFirstTick={true} xAxisTicks={xAxisTicks} />
        </>
      )}
      <ResponsiveContainer width={!roundComplete ? graphWidth : '95%'} height="100%">
        <BarChart data={forceGraphData}>
          <XAxis tick={false} dataKey="index" type="number" domain={[0, forceGraphData.length]} />
          <YAxis domain={[0, maxForce]} tick={false} hide={true} />
          <Bar
            barSize={10}
            dataKey="force"
            fill="#00BFB9"
            maxBarSize={20}
            shape={<CombinationBarShape />}
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  )
}

export default ForceGraphRound
